/**
 * Statement layouts — `/finance/statement-layouts`.
 *
 * How a statement is grouped is data, so this screen edits it. Three contract
 * facts drive the shape:
 *
 * A save creates a NEW VERSION and never replaces its predecessor, so versions
 * are listed rather than collapsed: a statement filed last quarter still
 * resolves to the version in force then, and seeing that history is the point.
 *
 * Only the name, notes and effective date can be changed in place. The code,
 * type and version are the provenance a filed statement cites, so they are
 * shown and never offered for edit.
 *
 * Validation is the blocking check and always answers with the COMPLETE issue
 * list. It is rendered whole — an operator fixing a chart of accounts needs
 * every hole at once, not the first one — and the counts are shown beside it so
 * "12 unmapped" is legible before reading twelve rows.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, CircleAlert, Copy, Plus } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Textarea,
} from "@erp/ui";

import { formatCalendarDate } from "@/lib/format";

import {
  describeRule,
  layoutTree,
  listStatementLayouts,
  updateStatementLayout,
  validateStatementLayout,
  type StatementLayout,
} from "../statement-layouts-api";
import { StatementLayoutEditor } from "./statement-layout-editor";

const LAYOUTS_KEY = ["finance/statement-layouts"];

export interface StatementLayoutsPageProps {
  canCreate?: boolean;
  canUpdate?: boolean;
}

export function StatementLayoutsPage({
  canCreate = false,
  canUpdate = false,
}: StatementLayoutsPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editorSeed, setEditorSeed] = useState<StatementLayout | null | undefined>(undefined);
  const [meta, setMeta] = useState<{ name: string; notes: string; valid_from: string } | null>(
    null,
  );
  const [savingMeta, setSavingMeta] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  const layouts = useQuery({
    queryKey: LAYOUTS_KEY,
    queryFn: async ({ signal }) => [...(await listStatementLayouts({}, signal)).items],
  });

  const selected = useMemo(
    () => (layouts.data ?? []).find((layout) => layout.id === selectedId) ?? null,
    [layouts.data, selectedId],
  );

  useEffect(() => {
    setMeta(
      selected === null
        ? null
        : {
            name: selected.name,
            notes: selected.notes ?? "",
            valid_from: selected.valid_from,
          },
    );
  }, [selected]);

  const validation = useQuery({
    queryKey: ["finance/statement-layout-validation", selectedId],
    queryFn: ({ signal }) => validateStatementLayout(selectedId as string, {}, signal),
    enabled: selectedId !== null && checking,
  });

  const day = (iso: string): string => {
    try {
      return formatCalendarDate(iso, i18n.language);
    } catch {
      return iso;
    }
  };

  const saveMeta = () => {
    if (selected === null || meta === null) return;
    setSavingMeta(true);
    setError(null);
    void updateStatementLayout(selected.id, {
      name: meta.name,
      valid_from: meta.valid_from,
      ...(meta.notes.trim() === "" ? {} : { notes: meta.notes }),
    })
      .then(() => queryClient.invalidateQueries({ queryKey: LAYOUTS_KEY }))
      .catch(() =>
        setError(t("finance.statementLayouts.saveError", "The layout could not be saved.")),
      )
      .finally(() => setSavingMeta(false));
  };

  const grouped = useMemo(() => {
    const byCode = new Map<string, StatementLayout[]>();
    for (const layout of layouts.data ?? []) {
      const versions = byCode.get(layout.code) ?? [];
      versions.push(layout);
      byCode.set(layout.code, versions);
    }
    return [...byCode.entries()].map(([code, versions]) => ({
      code,
      versions: [...versions].sort((left, right) => right.version - left.version),
    }));
  }, [layouts.data]);

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold tracking-tight">
            {t("finance.statementLayouts.title", "Statement layouts")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "finance.statementLayouts.subtitle",
              "How a statement is grouped. Saving makes a new version; the one a filed statement cited stays exactly as it was.",
            )}
          </p>
        </div>
        {canCreate ? (
          <Button type="button" onClick={() => setEditorSeed(null)}>
            <Plus className="size-4" aria-hidden="true" />
            {t("finance.statementLayouts.new", "New layout")}
          </Button>
        ) : null}
      </div>

      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("finance.statementLayouts.versions", "Layouts and versions")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {layouts.isLoading ? (
              <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
            ) : grouped.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("finance.statementLayouts.empty", "No layout is defined.")}
              </p>
            ) : (
              grouped.map((group) => (
                <div key={group.code} className="space-y-1">
                  <p className="font-mono text-xs uppercase text-muted-foreground" dir="ltr">
                    {group.code}
                  </p>
                  <ul className="flex flex-col gap-1">
                    {group.versions.map((layout) => (
                      <li key={layout.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedId(layout.id);
                            setChecking(false);
                          }}
                          aria-current={layout.id === selectedId ? "true" : undefined}
                          className={`w-full rounded-md border p-2 text-start text-sm hover:bg-muted/50 ${
                            layout.id === selectedId ? "border-primary bg-muted/40" : ""
                          }`}
                        >
                          <span className="flex flex-wrap items-center justify-between gap-2">
                            <span>{layout.name}</span>
                            <Badge variant="secondary">
                              {t("finance.statementLayouts.version", "v{{version}}", {
                                version: layout.version,
                              })}
                            </Badge>
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            {t(
                              `finance.statementType.${layout.statement_type}`,
                              layout.statement_type,
                            )}{" "}
                            ·{" "}
                            {t("finance.statementLayouts.from", "from {{date}}", {
                              date: day(layout.valid_from),
                            })}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {selected === null ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">
                {t("finance.statementLayouts.pick", "Pick a layout to see how it groups.")}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="flex flex-col gap-4">
            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>{selected.name}</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge variant="outline">
                    <span dir="ltr" className="font-mono">
                      {selected.code}
                    </span>
                  </Badge>
                  <Badge variant="secondary">
                    {t(`finance.statementType.${selected.statement_type}`, selected.statement_type)}
                  </Badge>
                  <Badge variant="secondary">
                    {t("finance.statementLayouts.version", "v{{version}}", {
                      version: selected.version,
                    })}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {t(
                      "finance.statementLayouts.provenance",
                      "Code, type and version are what a filed statement cites, so they cannot move.",
                    )}
                  </span>
                </div>

                {canUpdate && meta !== null ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="layout-name">
                        {t("finance.statementLayouts.name", "Name")}
                      </Label>
                      <Input
                        id="layout-name"
                        value={meta.name}
                        onChange={(event) => setMeta({ ...meta, name: event.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="layout-valid-from">
                        {t("finance.statementLayouts.validFrom", "In force from")}
                      </Label>
                      <Input
                        id="layout-valid-from"
                        type="date"
                        dir="ltr"
                        value={meta.valid_from}
                        onChange={(event) => setMeta({ ...meta, valid_from: event.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="layout-notes">
                        {t("finance.statementLayouts.notes", "Notes")}
                      </Label>
                      <Textarea
                        id="layout-notes"
                        rows={2}
                        value={meta.notes}
                        onChange={(event) => setMeta({ ...meta, notes: event.target.value })}
                      />
                    </div>
                    <div className="flex gap-2 sm:col-span-2">
                      <Button
                        type="button"
                        disabled={savingMeta || meta.name.trim() === ""}
                        onClick={saveMeta}
                      >
                        {t("actions.save", "Save")}
                      </Button>
                      {canCreate ? (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setEditorSeed(selected)}
                        >
                          <Copy className="size-4" aria-hidden="true" />
                          {t("finance.statementLayouts.newVersion", "New version from this")}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ) : selected.notes === undefined || selected.notes === "" ? null : (
                  <p className="text-sm text-muted-foreground">{selected.notes}</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>{t("finance.statementLayouts.linesTitle", "Lines")}</h2>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[44rem] text-sm">
                    <thead>
                      <tr className="border-b text-xs uppercase text-muted-foreground">
                        <th scope="col" className="p-2 text-start">
                          {t("finance.statementLayouts.line", "Line")}
                        </th>
                        <th scope="col" className="p-2 text-start">
                          {t("finance.statementLayouts.kind", "Value from")}
                        </th>
                        <th scope="col" className="p-2 text-start">
                          {t("finance.statementLayouts.selects", "Selects")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {layoutTree(selected.lines).map(({ line, depth }) => (
                        <tr key={line.code} className="border-b last:border-0">
                          <td className="p-2">
                            <span
                              className="block"
                              style={{ paddingInlineStart: `${depth * 1.25}rem` }}
                            >
                              {line.label}
                              {line.sign_flip ? (
                                <Badge variant="outline" className="ms-2">
                                  {t("finance.statementLayouts.signFlip", "Shown positive")}
                                </Badge>
                              ) : null}
                            </span>
                            <span
                              className="block font-mono text-xs text-muted-foreground"
                              dir="ltr"
                              style={{ paddingInlineStart: `${depth * 1.25}rem` }}
                            >
                              {line.code}
                            </span>
                          </td>
                          <td className="p-2">
                            {t(`finance.statementLineKind.${line.kind}`, line.kind)}
                            {line.cash_flow_activity === undefined ? null : (
                              <Badge variant="secondary" className="ms-2">
                                {t(
                                  `finance.cashFlowActivity.${line.cash_flow_activity}`,
                                  line.cash_flow_activity,
                                )}
                              </Badge>
                            )}
                          </td>
                          <td className="p-2 text-xs">
                            {line.rules.length === 0 ? (
                              <span className="text-muted-foreground">—</span>
                            ) : (
                              <ul className="space-y-0.5">
                                {line.rules.map((rule, index) => (
                                  <li key={rule.id ?? index} dir="ltr" className="font-mono">
                                    {t(`finance.statementRuleKind.${rule.kind}`, rule.kind)}:{" "}
                                    {describeRule(rule)}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>{t("finance.statementLayouts.checkTitle", "Can it produce a statement?")}</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t(
                    "finance.statementLayouts.checkHint",
                    "Every postable account — and every retired account still carrying movement — must land on exactly one line. Every issue is listed at once.",
                  )}
                </p>
                <Button type="button" variant="outline" onClick={() => setChecking(true)}>
                  {t("finance.statementLayouts.check", "Check this layout")}
                </Button>
                {!checking ? null : validation.isLoading ? (
                  <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
                ) : validation.data === undefined ? (
                  <p className="text-sm text-destructive">
                    {t("finance.statementLayouts.checkError", "The check could not run.")}
                  </p>
                ) : (
                  <div className="space-y-3">
                    <p className="flex items-center gap-2 text-sm">
                      {validation.data.valid ? (
                        <CheckCircle2 className="size-4 text-success" aria-hidden="true" />
                      ) : (
                        <CircleAlert className="size-4 text-destructive" aria-hidden="true" />
                      )}
                      {validation.data.valid
                        ? t(
                            "finance.statementLayouts.usable",
                            "This layout can produce a statement.",
                          )
                        : t(
                            "finance.statementLayouts.blocked",
                            "This layout cannot produce a statement yet.",
                          )}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t(
                        "finance.statementLayouts.counts",
                        "{{mapped}} of {{eligible}} accounts mapped · {{unmapped}} unmapped · {{untagged}} untagged",
                        {
                          mapped: validation.data.mapped_accounts,
                          eligible: validation.data.eligible_accounts,
                          unmapped: validation.data.unmapped_accounts,
                          untagged: validation.data.untagged_accounts,
                        },
                      )}
                    </p>
                    {validation.data.issues.length === 0 ? null : (
                      <ul className="space-y-1 text-sm">
                        {validation.data.issues.map((issue, index) => (
                          <li
                            key={`${issue.code}-${issue.account_id ?? index}`}
                            className="rounded-md border p-2"
                          >
                            <span className="block font-medium">
                              {t(
                                `finance.statementValidation.${issue.code}`,
                                issue.code.replace(/_/g, " "),
                              )}
                            </span>
                            <span className="block text-xs text-muted-foreground">
                              {issue.account_code === undefined ? null : (
                                <span dir="ltr" className="font-mono">
                                  {issue.account_code}{" "}
                                </span>
                              )}
                              {issue.detail}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {editorSeed === undefined ? null : (
        <StatementLayoutEditor
          seed={editorSeed}
          onClose={() => setEditorSeed(undefined)}
          onSaved={(layout) => {
            setSelectedId(layout.id);
            setChecking(false);
            void queryClient.invalidateQueries({ queryKey: LAYOUTS_KEY });
          }}
        />
      )}
    </div>
  );
}
